/**
 * Timed Storage Utility
 *
 * Provides helpers for setting and retrieving localStorage/sessionStorage entries
 * with an automatic expiration time (TTL).
 *
 * Features:
 * - Supports both localStorage and sessionStorage.
 * - Stores an `expiresAt` timestamp alongside the value.
 * - Automatically removes expired entries when accessed.
 * - Automatically JSON-stringifies plain objects before storage.
 * - Automatically parses JSON strings back into objects on retrieval.
 *
 * Usage:
 *   // Store a value in localStorage that expires in 5 seconds
 *   setTimedLocalStorage('foo', 'bar', 5 * 1000);
 * 
 *   // Returns stored value or null if expired
 *   const value = getTimedLocalStorage('key'); 
 */

import _ from 'lodash';
import { MoUtils } from 'ui-common';

type StorageType = 'local' | 'session';

interface TimedStorageValue {
  value: any;
  expiresAt: number;
}

const setTimedStorage = (type: StorageType, key: string, value: any, timeInMs: number): void => {
  const storage = type === 'local' ? localStorage : sessionStorage;
  const expiresAt = Date.now() + timeInMs;
  const storedValue = _.isPlainObject(value) ? JSON.stringify(value) : value;
  const payload: TimedStorageValue = { value: storedValue, expiresAt };

  storage.setItem(key, JSON.stringify(payload));
};

const getTimedStorage = (type: StorageType, key: string) => {
  const storage = type === 'local' ? localStorage : sessionStorage;
  const raw = storage.getItem(key);

  if (!raw) return null;

  const payload: TimedStorageValue = MoUtils.safeParseJSON(raw);
  let retrievedValue = null;

  if (_.isPlainObject(payload) && _.has(payload, 'value') && _.has(payload, 'expiresAt')) {
    if (Date.now() > payload.expiresAt) {
      storage.removeItem(key);
    } else {
      retrievedValue = MoUtils.safeParseJSON(payload.value);
    }
  }

  return retrievedValue;
};

export const setTimedLocalStorage = (key: string, value: any, timeInMs: number) => {
  return setTimedStorage('local', key, value, timeInMs);
};

export const getTimedLocalStorage = (key: string) => {
  return getTimedStorage('local', key);
};

export const setTimedSessionStorage = (key: string, value: any, timeInMs: number) => {
  return setTimedStorage('session', key, value, timeInMs);
};

export const getTimedSessionStorage = (key: string) => {
  return getTimedStorage('session', key);
};